import {
  DeleteOutlined,
  EditOutlined,
  PictureOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS, VehicleCategory, VehicleStatus } from '@tms/shared';

import { api } from '@/api/client';
import { useApiMutation, useDebouncedValue, usePaged } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { DepartmentFilter } from '@/components/DepartmentSelect';
import { createdAtColumn } from '@/components/createdAtColumn';
import { CheckSelect, listParam, StatusFilter } from '@/components/StatusFilter';
import { StickyTable } from '@/components/StickyTable';
import { TableCard } from '@/components/TableCard';
import { CATEGORY_LABEL, STATUS_COLOR, STATUS_LABEL, fmt } from '@/lib/labels';

import { VehicleDrawer } from './vehicles/VehicleDrawer';
import { VehicleFormModal, type VehicleFormValues } from './vehicles/VehicleFormModal';
import { VehiclePhotoPreview, usePhotoPreview } from './vehicles/VehiclePhotoPreview';

interface VehicleRow {
  id: number;
  garageNumber: string;
  plateNumber: string | null;
  vin: string | null;
  inventoryNumber: string | null;
  category: string;
  status: string;
  ownership: string;
  meterType: string;
  tankCapacity: string | null;
  modelId: number;
  departmentId: number | null;
  fuelTypeId: number | null;
  manufactureYear: number | null;
  commissionedAt: string | null;
  requiredPermitTypeId: number | null;
  notes: string | null;
  currentOdometer: string | null;
  currentEngineHours: string | null;
  currentFuelLevel: string;
  createdAt: string;
  model: { manufacturer: string; model: string } | null;
  department: { name: string } | null;
  /** Главное фото, если оно загружено. Список отдаёт только его идентификатор. */
  photos: Array<{ id: number }>;
}

export function VehiclesPage() {
  const { can } = useAuth();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput);
  const [status, setStatus] = useState<string[]>([]);
  const [category, setCategory] = useState<string[]>([]);
  const [departmentId, setDepartmentId] = useState<number[]>([]);
  // Несколько значений уходят на сервер одной строкой через запятую.
  const filters = {
    search: search || undefined,
    status: listParam(status),
    category: listParam(category),
    departmentId: listParam(departmentId),
  };

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<VehicleFormValues | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const preview = usePhotoPreview();

  // Подразделения берём из общего справочника: он уже кэширован формой
  // постановки на учёт, повторного запроса ради фильтра не будет.

  const query = usePaged<VehicleRow>(
    ['vehicles'],
    '/vehicles',
    { page, pageSize, ...filters },
  );

  const remove = useApiMutation(
    async (id: number) => {
      const { data } = await api.delete(`/vehicles/${id}`);
      return data;
    },
    { successMessage: t('Техника списана с учёта'), invalidate: [['vehicles'], ['office-summary']] },
  );

  if (!can(PERMISSIONS.VEHICLE_READ)) {
    return <Typography.Text type="danger">{t('Нет прав на просмотр транспорта')}</Typography.Text>;
  }

  return (
    <TableCard
      title={t('Транспорт и спецтехника')}
      extra={
        <Space wrap>
          <Input.Search
            allowClear
            placeholder={t('Гаражный номер, госномер, VIN')}
            style={{ width: 260 }}
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              setPage(1);
            }}
          />
          <CheckSelect
            placeholder={t('Категория')}
            width={210}
            value={category}
            onChange={(value) => {
              setCategory(value);
              setPage(1);
            }}
            options={Object.values(VehicleCategory).map((c) => ({
              value: c,
              label: t(CATEGORY_LABEL[c] ?? c),
            }))}
          />
          <DepartmentFilter
            value={departmentId}
            onChange={(value) => {
              setDepartmentId(value);
              setPage(1);
            }}
          />
          <StatusFilter
            width={170}
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            options={Object.values(VehicleStatus).map((s) => ({
              value: s,
              label: t(STATUS_LABEL[s] ?? s),
              color: STATUS_COLOR[s],
            }))}
          />
          {can(PERMISSIONS.VEHICLE_CREATE) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              {t('Поставить на учёт')}
            </Button>
          )}
        </Space>
      }
    >
      <StickyTable<VehicleRow>
        rowKey="id"
        exportSource={{
          url: '/vehicles',
          params: filters,
        }}
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        onRow={(row) => ({
          onClick: () => setDetailId(row.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${t('Всего:')} ${total}`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
        columns={[
          {
            title: t('Статус'),
            dataIndex: 'status',
            width: 120,
            render: (value: string) => (
              <Tag color={STATUS_COLOR[value] ?? 'default'}>{t(STATUS_LABEL[value] ?? value)}</Tag>
            ),
          },
          {
            title: t('Модель'),
            dataIndex: 'model',
            // Жирным — по этой колонке строку находят глазами.
            render: (model: VehicleRow['model']) =>
              model ? <span className="tms-cell-key">{`${model.manufacturer} ${model.model}`}</span> : '—',
          },
          { title: t('Гаражный'), dataIndex: 'garageNumber', width: 120 },
          { title: t('Госномер'), dataIndex: 'plateNumber', width: 140 },
          {
            title: t('Категория'),
            dataIndex: 'category',
            render: (value: string) => t(CATEGORY_LABEL[value] ?? value),
          },
          {
            title: t('Одометр, км'),
            exportTotal: false,
            dataIndex: 'currentOdometer',
            width: 120,
            align: 'right',
            render: (value: string | null) => fmt(value),
          },
          {
            title: t('Моточасы'),
            exportTotal: false,
            dataIndex: 'currentEngineHours',
            width: 110,
            align: 'right',
            render: (value: string | null) => fmt(value),
          },
          {
            title: t('В баке, л'),
            dataIndex: 'currentFuelLevel',
            width: 100,
            align: 'right',
            render: (value: string) => fmt(value, 1),
          },
          {
            title: t('Фото'),
            key: 'photo',
            width: 70,
            align: 'center',
            render: (_: unknown, row: VehicleRow) => {
              const photo = row.photos?.[0];
              if (!photo) {
                // Пустая ячейка, а не серая иконка: колонка должна с одного
                // взгляда показывать, у какой техники снимок есть.
                return null;
              }
              return (
                <Tooltip title={t('Показать фото')}>
                  <button
                    type="button"
                    className="tms-photo-icon-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      preview.open(row.id, photo.id);
                    }}
                  >
                    <PictureOutlined />
                  </button>
                </Tooltip>
              );
            },
          },
          createdAtColumn<VehicleRow>(t),
          {
            title: '',
            width: 130,
            render: (_: unknown, row: VehicleRow) => (
              <Space size={0} onClick={(event) => event.stopPropagation()}>
                {can(PERMISSIONS.VEHICLE_UPDATE) && (
                  <Tooltip title={t('Изменить')}>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditing({
                          id: row.id,
                          garageNumber: row.garageNumber,
                          plateNumber: row.plateNumber ?? undefined,
                          vin: row.vin ?? undefined,
                          inventoryNumber: row.inventoryNumber ?? undefined,
                          category: row.category,
                          modelId: row.modelId,
                          departmentId: row.departmentId ?? undefined,
                          fuelTypeId: row.fuelTypeId ?? undefined,
                          meterType: row.meterType,
                          ownership: row.ownership,
                          tankCapacity: row.tankCapacity ? Number(row.tankCapacity) : undefined,
                          manufactureYear: row.manufactureYear ?? undefined,
                          commissionedAt: row.commissionedAt ?? undefined,
                          requiredPermitTypeId: row.requiredPermitTypeId,
                          notes: row.notes ?? undefined,
                          status: row.status,
                        });
                        setFormOpen(true);
                      }}
                    />
                  </Tooltip>
                )}
                {can(PERMISSIONS.VEHICLE_DELETE) && (
                  <Popconfirm
                    title={t('Списать технику с учёта?')}
                    description={t('Документы за прошлые периоды сохранятся.')}
                    okText={t('Списать')}
                    cancelText={t('Отмена')}
                    onConfirm={() => remove.mutate(row.id)}
                  >
                    <Tooltip title={t('Списать')}>
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      <VehicleFormModal open={formOpen} initial={editing} onClose={() => setFormOpen(false)} />
      <VehicleDrawer vehicleId={detailId} onClose={() => setDetailId(null)} />

      {preview.target && (
        <VehiclePhotoPreview
          open
          vehicleId={preview.target.vehicleId}
          photoId={preview.target.photoId}
          onClose={preview.close}
        />
      )}

    </TableCard>
  );
}
